import { createContext, ReactNode, useCallback, useEffect, useMemo, useReducer } from 'react';
import Keycloak from 'keycloak-js';
// @types
import { ActionMapType, AuthStateType, AuthUserType, KeycloakContextType } from './types';
// routes
import { PATH_AUTH } from '../routes/paths';
import { setTokens } from '../redux/slices/token';
import { DEV_MODE, KEYCLOAK_CONFIG } from '../config-global';
import axios from '../utils/axios';

// ----------------------------------------------------------------------

// #Init# kc.userInfo:{"sub":"7a79da45-feb9-4395-b8ce-8c9a6633b077","email_verified":true,"schools":[1,2],"user_name":"frank@schnegg.net","name":"Frank Rittinger","phone_number":"0815","preferred_username":"frank@schnegg.net","given_name":"Frank","family_name":"Rittinger","email":"frank@schnegg.net"}
type KeycloakUserInfo = {
  sub: string;
  email_verified: boolean;
  schools: [number];
  user_name: string; // email
  name: string;
  phone_number: string;
  preferred_username: string;
  given_name: string;
  family_name: string;
  email: string;
};

console.log(`### KEYCLOAK_CONFIG: ${KEYCLOAK_CONFIG}`);
// @ts-ignore
const keycloakClient = new Keycloak(KEYCLOAK_CONFIG);

const initialState: AuthStateType = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

enum Types {
  init = 'INITIALIZE',
  login = 'LOGIN',
  logout = 'LOGOUT',
}

type KeycloakAuthPayload = {
  [Types.init]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.login]: {
    user: AuthUserType;
  };
  [Types.logout]: undefined;
};

type KeycloakActions = ActionMapType<KeycloakAuthPayload>[keyof ActionMapType<KeycloakAuthPayload>];

const devUser = {
  id: '1',
  username: 'terra',
  email: 'terra@terraplan.com',
  firstName: 'Terra',
  lastName: 'Plan',
  enabled: true,
  emailVerified: true,
  roles: [
    'transport-write',
    'tour-write',
    'config-read',
    'export-create',
    'config-write',
    'export-read',
    'timetable-write',
    'timetable-read',
    'school-absence',
    'school-read',
    'traffic-read',
    'traffic-write',
    'school-write',
    'offline_access',
    'default-roles-terra',
    'uma_authorization',
    'tour-read',
    'superuser',
    'transport-read',
    'employeeAppDispo-read',
    'employeeAppDispo-write',
    'employeeAppDispo-export',
  ],
  totp: true, // FIXME Frank: what is totp?!
};

const reducer = (state: AuthStateType, action: KeycloakActions) => {
  if (action.type === Types.init) {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }
  if (action.type === Types.login) {
    const { user } = action.payload;
    return { ...state, isAuthenticated: true, user };
  }
  if (action.type === Types.logout) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

export const AuthContext = createContext<KeycloakContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      await keycloakClient.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        // onLoad: 'login-required'
      });

      console.log(`#Init# kc.authenticated: ${keycloakClient.authenticated}`);

      if (keycloakClient.authenticated) {
        if (keycloakClient.token && keycloakClient.refreshToken) {
          setTokens(keycloakClient.token, keycloakClient.refreshToken);
          axios.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
        }

        // This is actually a hack because it keeps the users session alive, even if the user is afk.
        setInterval(() => {
          keycloakClient
            .updateToken(50)
            .then((refreshed: any) => {
              if (refreshed) {
                console.log(`### token refreshed ${new Date()}`);
                if (keycloakClient.token && keycloakClient.refreshToken) {
                  setTokens(keycloakClient.token, keycloakClient.refreshToken);
                  axios.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
                }
              }
            })
            .catch(() => {
              console.log(`### Failed to refresh token ${new Date()}`);
              keycloakClient?.logout();
              window.location.href = PATH_AUTH.login;
              dispatch({ type: Types.logout });
            });
        }, 30000);

        console.log(`#Init# kc.subject: ${keycloakClient.subject}`);
        await keycloakClient.loadUserProfile();
        const rolesJson = JSON.stringify(keycloakClient.realmAccess?.roles);
        const user = keycloakClient.profile as AuthUserType;
        const profileJson = JSON.stringify(keycloakClient.profile);
        console.log(`### KC init, roles: ${rolesJson}`);
        console.log(`### KC init, profile: ${profileJson}`);

        await keycloakClient.loadUserInfo();
        const userInfo = keycloakClient.userInfo as KeycloakUserInfo;
        const userInfoJson = JSON.stringify(userInfo);
        console.log(`### KC init, userInfo: ${userInfoJson}`);

        if (user) {
          user.schools = userInfo.schools; // FIXME: do we need this information here?
        }

        dispatch({
          type: Types.init,
          payload: { isAuthenticated: true, user: user || null },
        });
      } else {
        dispatch({
          type: Types.init,
          payload: { isAuthenticated: false, user: null },
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: Types.init,
        payload: { isAuthenticated: false, user: null },
      });
    }
  }, []);

  useEffect(() => {
    if (DEV_MODE) {
      console.log('### Dev mode!');
      dispatch({
        type: Types.init,
        payload: { isAuthenticated: true, user: devUser },
      });
    } else {
      initialize();
    }
  }, [initialize]);

  // FIXME: this is actually never called!
  const login = useCallback(async () => {
    if (DEV_MODE) {
      setTokens('DEV_TOKEN', 'DEV_REFRESH_TOKEN');
      dispatch({
        type: Types.init,
        payload: { isAuthenticated: true, user: devUser },
      });
    } else {
      await keycloakClient.login();

      const isAuthenticated = keycloakClient?.authenticated;
      if (isAuthenticated) {
        await keycloakClient.loadUserProfile();
        console.log(`### Login kc.profile.email: ${keycloakClient.profile?.email}`);
        const user = keycloakClient.profile;
        if (keycloakClient.token && keycloakClient.refreshToken) {
          setTokens(keycloakClient.token, keycloakClient.refreshToken);
          axios.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
        }
        dispatch({ type: Types.login, payload: { user: user || null } });
      }
    }
  }, []);

  const logout = useCallback(() => {
    keycloakClient?.logout();
    window.location.href = PATH_AUTH.login;
    dispatch({ type: Types.logout });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      ...state,
      method: 'keycloak',
      user: {
        id: state?.user?.id,
        photoURL: state?.user?.picture,
        email: state?.user?.email,
        displayName: `${state?.user?.firstName} ${state?.user?.lastName}`,
        roles: DEV_MODE ? state?.user?.roles : keycloakClient?.realmAccess?.roles,
      },
      login,
      logout,
    }),
    [state, login, logout]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
